/**
 * UF-2 (UFC-FOLLOWUP-PRESET-RULES) — preset library for the
 * EvidenceConflictsPanel.
 *
 * Why: live-prod walkthrough surfaces common patterns the user hits
 * repeatedly across sites:
 *   - "Always prefer provider for `regulatory.*`" — regulatory data
 *     comes from authoritative APIs (FEMA, USGS, EPA); doc extracts
 *     are usually stale screenshots.
 *   - "Always prefer document for `siteProfile.*`" — site profile
 *     fields (acres, address, owner) come from the deal docs first;
 *     provider geocoding is a fallback.
 *
 * Without UF-2 the user has to open the per-row dropdown N times
 * across M sites. With UF-2 they pick a preset once and every
 * matching group lights up with an "Auto-resolve (preset)" badge;
 * one Apply click batches the lot through `persistSedConflicts`
 * (HARD-RULE-8 — same atomic contract as UF-1).
 *
 * Storage:
 *   - Per-user: `da:conflictPresets:user:<uid>` in localStorage.
 *   - Company-level presets surface as read-only entries the admin
 *     can curate (stub: same localStorage key under `:company:<id>`).
 *     The full Firestore-backed company-level store is deferred —
 *     this file ships the contract + the LocalStorage backing so the
 *     UI can land today.
 *
 * Presets are ADVISORY (HARD-RULE-9). Application is always explicit:
 * the panel renders the matched badge + an Apply CTA. No auto-resolve
 * on mount. Toggling a preset on/off only changes the badge.
 *
 * Pure / no React.
 */
import type { BulkStrategy } from './bulkConflictResolve';
import type { FieldConflictGroup } from './conflictGrouping';

/** One row of a preset — matches a `(sectionId, fieldKey)` pair via
 *  glob-style prefix wildcards. `*` matches anything; `regulatory.*`
 *  matches every section beginning with `regulatory`. Trailing-only
 *  wildcards by design — full RegExp would invite ReDoS + complicate
 *  the admin UI later. */
export interface PresetRule {
  /** Glob — e.g. `'regulatory'`, `'regulatory.*'`, `'*'`. Empty
   *  string treats as `'*'` (match-all). */
  sectionPattern: string;
  /** Glob over fieldKey. Same semantics as sectionPattern. */
  fieldPattern: string;
  /** Resolution applied to every member of every group the rule
   *  matches. `'prefer-doc-first'` falls back to provider on
   *  doc-less groups (UF-1 semantics carry through). */
  strategy: BulkStrategy;
}

export interface ConflictResolutionPreset {
  /** Stable id. Generated at create time (`uuid` or
   *  `pre-${Date.now()}-${rand}`); the panel never edits in-place
   *  without a fresh id (mutate-on-write keeps the rule history
   *  honest). */
  id: string;
  /** User-supplied label — surfaced in the menu + the per-group
   *  badge. */
  label: string;
  /** Free-form description — optional; surfaced as tooltip on the
   *  preset menu row. */
  description?: string;
  rules: PresetRule[];
  /** `'user'` = personal; `'company'` = curated, read-only in the
   *  user's library. Today both land in localStorage; the
   *  `'company'` ones are seeded by an admin write (TBD). */
  scope: 'user' | 'company';
  /** ISO 8601 — when the preset was first saved. */
  createdAt: string;
  /** ISO 8601 — last edit. Equal to `createdAt` for fresh presets. */
  updatedAt: string;
}

/* ── Built-in presets ──────────────────────────────────────────────── */

/**
 * The two patterns called out in the UFC-FOLLOWUP-PRESET-RULES brief.
 * Surfaced as `scope: 'company'` so they're read-only by default —
 * the user can clone-then-edit if they want a tweaked version.
 */
export const BUILTIN_PRESETS: ConflictResolutionPreset[] = [
  {
    id: 'builtin:prefer-provider-regulatory',
    label: 'Trust providers for regulatory data',
    description:
      'Confirm provider values for every group under regulatory.* — FEMA, USGS, EPA, ACHP, etc. are authoritative APIs; doc extracts are usually stale screenshots.',
    rules: [{ sectionPattern: 'regulatory', fieldPattern: '*', strategy: 'prefer-provider' }],
    scope: 'company',
    createdAt: '2026-05-19T00:00:00.000Z',
    updatedAt: '2026-05-19T00:00:00.000Z',
  },
  {
    id: 'builtin:prefer-doc-site-profile',
    label: 'Trust documents for site profile',
    description:
      'Use the first document value for groups under siteProfile.* — site profile fields (acres, address, owner) come from the deal docs first; provider geocoding is a fallback. Doc-less groups fall back to provider.',
    rules: [{ sectionPattern: 'siteProfile', fieldPattern: '*', strategy: 'prefer-doc-first' }],
    scope: 'company',
    createdAt: '2026-05-19T00:00:00.000Z',
    updatedAt: '2026-05-19T00:00:00.000Z',
  },
];

/* ── Storage ───────────────────────────────────────────────────────── */

const USER_KEY_PREFIX = 'da:conflictPresets:user:';
const COMPANY_KEY_PREFIX = 'da:conflictPresets:company:';

/**
 * Load the user's stored presets + the built-ins. Caller passes the
 * actor uid (or `'anon'` for the signed-out fallback the panel uses
 * before auth hydrates). The returned list is the union; built-ins
 * surface first, then user presets newest-first.
 *
 * Soft-fail on JSON parse error — corrupt storage returns just the
 * built-ins. Never throws.
 */
export function loadPresets(uid: string | null | undefined): ConflictResolutionPreset[] {
  const out: ConflictResolutionPreset[] = [...BUILTIN_PRESETS];
  if (typeof window === 'undefined') return out;
  try {
    if (uid) {
      const raw = window.localStorage.getItem(USER_KEY_PREFIX + uid);
      if (raw) {
        const parsed: unknown = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          // Defensive: filter out anything that doesn't look like a
          // preset (storage may have been written by an older client).
          for (const p of parsed) {
            if (isPreset(p)) out.push(p);
          }
        }
      }
    }
  } catch {
    /* corrupt storage — fall through to built-ins. */
  }
  // newest-first user presets at the end of built-ins.
  return out.sort((a, b) => {
    if (a.scope === 'company' && b.scope !== 'company') return -1;
    if (b.scope === 'company' && a.scope !== 'company') return 1;
    return b.updatedAt.localeCompare(a.updatedAt);
  });
}

/**
 * Persist the user's preset library (excluding built-ins). Pure
 * over storage — caller already shaped the list.
 */
export function savePresets(
  uid: string,
  presets: ConflictResolutionPreset[],
): { ok: boolean; reason?: string } {
  if (typeof window === 'undefined') {
    return { ok: false, reason: 'no-window' };
  }
  try {
    const userOnly = presets.filter(p => p.scope === 'user');
    window.localStorage.setItem(
      USER_KEY_PREFIX + uid,
      JSON.stringify(userOnly),
    );
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: (e as Error)?.message ?? 'unknown' };
  }
}

/** Type guard — protects against corrupt storage payloads. */
function isPreset(v: unknown): v is ConflictResolutionPreset {
  if (!v || typeof v !== 'object') return false;
  const r = v as Record<string, unknown>;
  return (
    typeof r.id === 'string'
    && typeof r.label === 'string'
    && Array.isArray(r.rules)
    && (r.scope === 'user' || r.scope === 'company')
    && typeof r.createdAt === 'string'
    && typeof r.updatedAt === 'string'
  );
}

/* ── Rule matching ─────────────────────────────────────────────────── */

/**
 * Glob match — `*` matches anything; otherwise the pattern is
 * treated as a prefix match against the input. Empty pattern
 * (`''`) is treated as `*` so a preset with `sectionPattern: ''`
 * means "any section".
 *
 * Trailing `.*` after a literal prefix is supported by the prefix
 * rule (e.g. `'regulatory.*'` matches every key starting with
 * `'regulatory'` — the `.*` suffix is dropped before the prefix
 * test). No mid-string globs by design.
 */
export function matchGlob(pattern: string, value: string): boolean {
  if (!pattern || pattern === '*') return true;
  const trimmed = pattern.endsWith('.*') ? pattern.slice(0, -2) : pattern;
  if (!trimmed) return true;
  return value === trimmed || value.startsWith(trimmed + '.') || value.startsWith(trimmed);
}

/**
 * UF-2 — find the FIRST matching rule (within a single preset) for a
 * given (sectionId, fieldKey). Rules are evaluated in declaration
 * order so the user can put a narrow rule above a broad one. Returns
 * `undefined` when nothing matches.
 */
export function findMatchingRule(
  preset: ConflictResolutionPreset,
  sectionId: string,
  fieldKey: string,
): PresetRule | undefined {
  for (const r of preset.rules) {
    if (matchGlob(r.sectionPattern, sectionId) && matchGlob(r.fieldPattern, fieldKey)) {
      return r;
    }
  }
  return undefined;
}

/**
 * UF-2 — project `fieldGroups → Map<groupKey, { strategy }>` for one
 * preset. Drives the per-row "Auto-resolve (preset)" badge AND the
 * Apply CTA's batch.
 *
 * Groups that match get a strategy assignment; unmatched groups are
 * absent from the map (no entry === no preset applies). This shape
 * lets the panel render the badge without re-walking the preset for
 * every paint.
 */
export interface PresetMatch {
  rule: PresetRule;
  strategy: BulkStrategy;
}

export function projectPresetMatches(
  preset: ConflictResolutionPreset | null | undefined,
  groups: readonly FieldConflictGroup[],
): Map<string, PresetMatch> {
  const out = new Map<string, PresetMatch>();
  if (!preset) return out;
  for (const g of groups) {
    const r = findMatchingRule(preset, g.sectionId, g.fieldKey);
    if (r) out.set(g.key, { rule: r, strategy: r.strategy });
  }
  return out;
}
